import React, { useEffect, useState } from 'react';
import api from '../../../../config/axios';

import ContaModal from './modals/Conta';
import Paginacao from '../../../../components/utils/paginacao';

import { FaPlus, FaEdit, FaFilter, FaTrashAlt, FaArrowLeft } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';

const Contas = () => {
    const navigate = useNavigate();
    const [error, setError] = useState(null);

    const [contas, setContas] = useState([]);
    const [filtroNome, setFiltroNome] = useState('');
    const [filtroTipo, setFiltroTipo] = useState('');

    const [selectedConta, setSelectedConta] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);

    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalRegistros, setTotalRegistros] = useState(0);
    const [saldos, setSaldos] = useState({});
    const limit = 10;

    const tiposConta = [
        { value: '', label: 'Todos os tipos' },
        { value: 'corrente', label: 'Corrente' },
        { value: 'poupanca', label: 'Poupança' },
        { value: 'investimento', label: 'Investimento' }
    ];

    const fetchContas = async (currentPage) => {
        try {
            Swal.fire({
                title: 'Carregando...',
                allowOutsideClick: false,
                allowEscapeKey: false,
                showConfirmButton: false,
                didOpen: () => {
                    Swal.showLoading();
                }
            });

            const params = {
                page: currentPage,
                limit: limit,
                nome_exibicao: filtroNome,
                tipo_conta: filtroTipo
            };

            const response = await api.get('/contas-bancarias', { params });

            const contasData = response.data.records || [];
            setContas(contasData);
            setTotalPages((Math.ceil(response.data.totalRegistros / limit)) || 1);
            setTotalRegistros(response.data.totalRegistros || 0);
            setError(null);

            fetchSaldos(contasData);
        } catch (err) {
            setError('Erro ao carregar os dados. Tente novamente mais tarde.');
        } finally {
            Swal.close();
        }
    };

    const fetchSaldos = async (contasData) => {
        const saldosTemp = {};
        for (const conta of contasData) {
            try {
                const response = await api.get(`/contas-bancarias/${conta.id}/saldo`);
                saldosTemp[conta.id] = response.data.saldo_atual;
            } catch (error) {
                saldosTemp[conta.id] = null;
            }
        }
        setSaldos(saldosTemp);
    };

    const formatarValor = (valor) => {
        if (valor === null || valor === undefined) return '-';
        return parseFloat(valor).toLocaleString('pt-BR', {
            style: 'currency',
            currency: 'BRL'
        });
    };

    useEffect(() => {
        fetchContas(currentPage);
    }, [currentPage]);

    const handlePageChange = (currentPage) => {
        setCurrentPage(currentPage);
    };

    const handleAddConta = () => {
        setSelectedConta(null);
        setIsModalOpen(true);
    };

    const handleEditConta = (conta) => {
        setSelectedConta(conta);
        setIsModalOpen(true);
    };

    const handleDeleteConta = async (conta) => {
        const result = await Swal.fire({
            title: 'Tem certeza?',
            text: `Deseja excluir a conta "${conta.nome_exibicao}"?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Sim, excluir!',
            cancelButtonText: 'Cancelar'
        });

        if (result.isConfirmed) {
            try {
                await api.delete(`/contas-bancarias/${conta.id}`);
                Swal.fire('Excluído!', 'Conta foi excluída com sucesso.', 'success');
                fetchContas(currentPage);
            } catch (error) {
                Swal.fire('Erro!', error.response.data.message || 'Não foi possível excluir a conta.', 'error');
            }
        }
    };

    const handleFiltrar = () => {
        setCurrentPage(1);
        fetchContas(1);
    };

    const handleLimparFiltros = () => {
        setFiltroNome('');
        setFiltroTipo('');
    };

    const handleModalClose = () => {
        setIsModalOpen(false);
        setSelectedConta(null);
    };

    const handleContaAtualizada = () => {
        fetchContas(currentPage);
        handleModalClose();
    };

    const formatarTipoConta = (tipo) => {
        const tipos = {
            corrente: 'Corrente',
            poupanca: 'Poupança',
            investimento: 'Investimento'
        };
        return tipos[tipo] || tipo;
    };

    return (
        <div className="container-fluid">
            <div className="card">
                <div className="card-body">
                    <div className="row mt-3">
                        <div className="col-12">
                            <div className="d-flex align-items-center">
                                <button
                                    className="btn btn-outline-secondary me-3"
                                    onClick={() => navigate('/financeiro/cadastros')}
                                    title="Voltar para Cadastros"
                                >
                                    <FaArrowLeft />
                                </button>
                                <h4 className="fw-bold mb-0">Contas Bancárias</h4>
                            </div>
                        </div>
                    </div>
                    <div className="row mt-3 mb-3">
                        <div className="col-md-6 col-12">
                            <button className="btn btn-outline-primary" onClick={handleAddConta}>
                                <FaPlus /> Adicionar Conta
                            </button>
                        </div>
                    </div>

                    <div className="row mt-4 mb-4">
                        <div className="col-lg-3 col-12">
                            <label className="form-label fw-bold">Nome da Conta</label>
                            <input
                                type="text"
                                autoComplete="off"
                                placeholder="Digite o nome da conta"
                                value={filtroNome}
                                className="form-control"
                                onChange={(e) => setFiltroNome(e.target.value)}
                            />
                        </div>
                        <div className="col-lg-3 col-12 mt-lg-0 mt-3">
                            <label className="form-label fw-bold">Tipo de Conta</label>
                            <select
                                className="form-select"
                                value={filtroTipo}
                                onChange={(e) => setFiltroTipo(e.target.value)}
                            >
                                {tiposConta.map(tipo => (
                                    <option key={tipo.value} value={tipo.value}>
                                        {tipo.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="col-lg-6 col-12 mt-lg-0 mt-3 d-flex align-items-end">
                            <div className="d-flex gap-2 w-100 justify-content-end">
                                <button
                                    className="btn btn-outline-secondary"
                                    onClick={handleLimparFiltros}
                                >
                                    Limpar
                                </button>
                                <button
                                    className="btn btn-custom-primary"
                                    onClick={handleFiltrar}
                                >
                                    <FaFilter /> Filtrar
                                </button>
                            </div>
                        </div>
                    </div>

                    {error ? (
                        <p className="error-message">{error}</p>
                    ) : (
                        <div className="table-responsive">
                            <table className="table table-striped">
                                <thead className="table-custom-primary">
                                    <tr>
                                        <th>Banco</th>
                                        <th className="text-nowrap">Nome da Conta</th>
                                        <th>Agência</th>
                                        <th className="text-nowrap">Número</th>
                                        <th>Tipo</th>
                                        <th className="text-nowrap">Saldo Atual</th>
                                        <th>Status</th>
                                        <th className="text-center">Ações</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {contas.length === 0 ? (
                                        <tr>
                                            <td colSpan="8" className="text-center">
                                                Nenhuma conta encontrada.
                                            </td>
                                        </tr>
                                    ) : (
                                        contas.map((conta, index) => (
                                            <tr key={index}>
                                                <td>{conta.banco?.nome || conta.Banco?.nome || 'N/A'}</td>
                                                <td className="text-nowrap">{conta.nome_exibicao}</td>
                                                <td>{conta.agencia || '-'}</td>
                                                <td className="text-nowrap">{conta.numero || '-'}</td>
                                                <td>
                                                    <span className={`badge ${conta.tipo_conta === 'corrente' ? 'bg-primary' :
                                                            conta.tipo_conta === 'poupanca' ? 'bg-success' :
                                                                'bg-info'
                                                        }`}>
                                                        {formatarTipoConta(conta.tipo_conta)}
                                                    </span>
                                                </td>
                                                <td className={`text-nowrap fw-bold ${saldos[conta.id] >= 0 ? 'text-success' : 'text-danger'}`}>
                                                    {formatarValor(saldos[conta.id])}
                                                </td>
                                                <td>
                                                    <span className={`badge ${conta.ativo ? 'bg-success' : 'bg-danger'}`}>
                                                        {conta.ativo ? 'Ativo' : 'Inativo'}
                                                    </span>
                                                </td>
                                                <td className="text-center">
                                                    <div className="d-flex gap-2 justify-content-center">
                                                        <button
                                                            className="btn btn-custom-secondary btn-sm"
                                                            onClick={() => handleEditConta(conta)}
                                                            title="Editar"
                                                        >
                                                            <FaEdit />
                                                        </button>
                                                        <button
                                                            className="btn btn-custom-secondary btn-sm ms-3"
                                                            onClick={() => handleDeleteConta(conta)}
                                                            title="Excluir"
                                                        >
                                                            <FaTrashAlt />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}

                    <div className="d-flex justify-content-between align-items-center mt-4">
                        <div className="mb-3">
                            <span>
                                Total de registros: <strong>{totalRegistros}</strong>
                            </span>
                        </div>
                        <Paginacao
                            currentPage={currentPage}
                            totalPages={totalPages}
                            onPageChange={handlePageChange}
                        />
                    </div>
                </div>
            </div>

            {isModalOpen && (
                <ContaModal
                    conta={selectedConta}
                    onClose={handleModalClose}
                    onContaAtualizada={handleContaAtualizada}
                />
            )}
        </div>
    );
};

export default Contas; 